import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Box, 
  Typography, 
  IconButton,
  Button,
  useTheme,
  Popper,
  Paper,
  ClickAwayListener,
  Grow
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import { RootState } from '../../store/store';
import { setTheme, ThemeMode } from '../../store/slices/themeSlice';
import { useAuth0 } from '@auth0/auth0-react';
import { logout as logoutAction } from '../../store/slices/sessionSlice';
import { setLanguage, Language } from '../../store/slices/languageSlice';
import { useTranslation } from '../../hooks/useTranslation';
import { setBackground, BackgroundMode, setCustomBackground, clearCustomBackground } from '../../store/slices/backgroundSlice';  

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
}

interface CustomDropdownProps<T extends string> {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}

const CustomDropdown = <T extends string>({ value, options, onChange }: CustomDropdownProps<T>) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleSelect = (selectedValue: T) => {
    onChange(selectedValue);
    setOpen(false);
  };

   

  const selectedLabel = options.find(option => option.value === value)?.label || '';

  return (
    <div>
      <Box
        ref={anchorRef}
        onClick={handleToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          minWidth: '120px',
          p: '5px 10px',
          borderRadius: '8px',
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.action.hover,
          '&:hover': {
            bgcolor: theme.palette.action.selected,
          },
          transition: 'all 0.2s ease',
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            color: theme.palette.text.secondary,
            fontWeight: 500
          }}
        >
          {selectedLabel}
        </Typography>
        <ExpandMoreIcon 
          fontSize="small" 
          sx={{ 
            color: theme.palette.text.secondary,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }} 
        />
      </Box>
      
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="top-start"
        transition
        disablePortal={false}
        style={{ zIndex: 1400 }}
        modifiers={[
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              boundary: 'viewport',
              rootBoundary: 'document',
            },
          }
        ]}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'top-start' ? 'bottom left' : 'bottom right',
            }}
          >
            <Paper elevation={3} sx={{ 
              mb: 1, 
              minWidth: anchorRef.current ? anchorRef.current.clientWidth : 120,
              maxHeight: '200px',
              overflowY: 'auto',
              bgcolor: theme.palette.background.paper,
              borderRadius: '8px',
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.action.disabled,
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
            }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Box sx={{ py: 0.5 }}>
                  {options.map((option) => (
                    <Box
                      key={option.value}
                      onClick={() => handleSelect(option.value as T)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontWeight: option.value === value ? 500 : 400,
                        }}
                      >
                        {option.label}
                      </Typography>
                      {option.value === value && (
                        <CheckIcon
                          fontSize="small"
                          sx={{ color: theme.palette.primary.contrastText }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

const SettingsModal: React.FC<SettingsModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { logout: auth0Logout } = useAuth0();
  const themeMode = useSelector((state: RootState) => state.theme.mode);
  const selectedLanguage = useSelector((state: RootState) => state.language.currentLanguage);
  const selectedBackground = useSelector((state: RootState) => {
    const mode = state.background.mode as any;
    // Handle legacy values from localStorage
    if (mode === 'color' || mode === 'image') {
      return mode === 'color' ? 'none' : 'default';
    }
    return mode as BackgroundMode;
  });
  const theme = useTheme();
  const { t } = useTranslation();
  
  const handleThemeChange = (value: ThemeMode) => {
    dispatch(setTheme(value));
  };

  const handleLanguageChange = (value: Language) => {
    dispatch(setLanguage(value));
  };

  const handleLogout = () => {
    dispatch(logoutAction()); // Clear Redux session state
    auth0Logout({ 
      logoutParams: {
        returnTo: window.location.origin 
      }
    }); // Logout from Auth0
    onClose();
  };

  const handleBackgroundChange = (value: BackgroundMode) => {
    dispatch(setBackground(value));
  };
  
  const themeOptions = [
    { value: 'light' as ThemeMode, label: t('light') },
    { value: 'dark' as ThemeMode, label: t('dark') },
    { value: 'blue' as ThemeMode, label: t('blue') }
  ];

  const languageOptions = [
    { value: 'auto-detect' as Language, label: 'Auto-detect' },
    { value: 'english' as Language, label: 'English' },
    { value: 'arabic' as Language, label: 'Arabic' }
  ];

   

  

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "15px",
          bgcolor: theme.palette.background.paper,
          minHeight: 'auto',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        px: 3, 
        py: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Typography variant="h6" component="div" fontWeight="500" color={theme.palette.text.secondary}>
          {t('settings')}
        </Typography>
        <IconButton edge="end" onClick={onClose} aria-label="close" sx={{ color: theme.palette.text.secondary, padding: 0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ 
            px: 3, 
            py: 2, 
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Typography variant="body1" color={theme.palette.text.secondary}>{t('language')}</Typography>
            <CustomDropdown<Language>
              value={selectedLanguage}
              options={languageOptions}
              onChange={handleLanguageChange}
            />
          </Box>
          
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ 
            px: 3, 
            py: 2, 
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Typography variant="body1" color={theme.palette.text.secondary}>{t('theme')}</Typography>
            <CustomDropdown<ThemeMode>
              value={themeMode}
              options={themeOptions}
              onChange={handleThemeChange}
            />
          </Box>

         
         <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ 
            px: 3, 
            py: 2, 
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Typography variant="body1" color={theme.palette.text.secondary}>
              Background
            </Typography>
            <CustomDropdown<BackgroundMode>
              value={selectedBackground}
              options={[
                { value: 'none', label: 'Color' },
                { value: 'default', label: 'Image' }
              ]}
              onChange={handleBackgroundChange}
            />
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ 
            px: 3, 
            py: 2, 
            borderBottom: `1px solid ${themeMode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`
          }}>
            <Typography variant="body1" color={themeMode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.87)'}>{t('logoutDevice')}</Typography>
            <Button 
              variant="contained"
              size="small"
              sx={{ 
                borderRadius: "20px", 
                textTransform: 'none',
                px: 3,
                py: 0.75,
                fontSize: '0.9rem',
                color: 'white',
                backgroundColor: '#d32f2f',
                '&:hover': {
                  backgroundColor: '#f44336',
                }
              }}
              onClick={handleLogout}
            >
              {t('logout')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal; 